'use client';

import { NativeModalPortalHostProvider } from '@/components/ui/overlay/native-modal-portal-host';
import { cn } from '@/lib/ui/utils';
import {
  type AnimationEvent as ReactAnimationEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

const drawerClassName =
  'cms-block-workspace-workspace-1-workspace-mobile-drawer';
const vaulAnimationDurationMs = 500;
const closeAnimationName = 'cmsWorkspaceMobileDrawerSlideToBottom';
const WorkspaceMobileDrawerOverlayHostContext =
  createContext<HTMLElement | null>(null);

let documentScrollLockCount = 0;
let restoreDocumentScroll: (() => void) | null = null;

function acquireDocumentScrollLock() {
  if (documentScrollLockCount === 0) {
    const body = document.body;
    const root = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousRootOverflow = root.style.overflow;
    const previousRootOverscrollBehavior = root.style.overscrollBehavior;
    body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
    root.style.overscrollBehavior = 'none';
    restoreDocumentScroll = () => {
      body.style.overflow = previousBodyOverflow;
      root.style.overflow = previousRootOverflow;
      root.style.overscrollBehavior = previousRootOverscrollBehavior;
    };
  }
  documentScrollLockCount += 1;
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    documentScrollLockCount = Math.max(0, documentScrollLockCount - 1);
    if (documentScrollLockCount === 0) {
      restoreDocumentScroll?.();
      restoreDocumentScroll = null;
    }
  };
}

export function useWorkspaceMobileDrawerOverlayHost() {
  return useContext(WorkspaceMobileDrawerOverlayHostContext);
}

export function WorkspaceMobileDrawer({
  bodyClassName,
  children,
  description,
  header = 'visible',
  id,
  onOpenChange,
  open,
  size = 'standard',
  title,
}: {
  bodyClassName?: string;
  children: ReactNode;
  description?: ReactNode;
  header?: 'visible' | 'sr-only';
  id: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  size?: 'content' | 'standard';
  title: ReactNode;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollUnlockRef = useRef<(() => void) | null>(null);
  const dragStartYRef = useRef<number | null>(null);
  const [overlayHost, setOverlayHost] = useState<HTMLElement | null>(null);
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (open) {
      scrollUnlockRef.current ??= acquireDocumentScrollLock();
      if (!dialog.open) {
        dialog.showModal();
      }
      return;
    }

    if (dialog.open) {
      closeTimerRef.current = setTimeout(() => {
        dialog.close();
        scrollUnlockRef.current?.();
        scrollUnlockRef.current = null;
        closeTimerRef.current = null;
      }, vaulAnimationDurationMs);
    } else {
      scrollUnlockRef.current?.();
      scrollUnlockRef.current = null;
    }

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [open]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      scrollUnlockRef.current?.();
      scrollUnlockRef.current = null;
    },
    []
  );

  const finishCloseAnimation = (
    event: ReactAnimationEvent<HTMLDialogElement>
  ) => {
    if (
      open ||
      event.target !== event.currentTarget ||
      event.animationName !== closeAnimationName
    ) {
      return;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (event.currentTarget.open) {
      event.currentTarget.close();
    }
    scrollUnlockRef.current?.();
    scrollUnlockRef.current = null;
  };

  const resetDrag = () => {
    const dialog = dialogRef.current;
    dragStartYRef.current = null;
    if (!dialog) {
      return;
    }
    dialog.style.removeProperty('--cms-workspace-mobile-drawer-drag-offset');
    delete dialog.dataset.dragging;
  };

  const handleDragStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragStartYRef.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
    if (dialogRef.current) {
      dialogRef.current.dataset.dragging = 'true';
    }
  };

  const handleDragMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragStartYRef.current === null || !dialogRef.current) {
      return;
    }
    const offset = Math.max(0, event.clientY - dragStartYRef.current);
    dialogRef.current.style.setProperty(
      '--cms-workspace-mobile-drawer-drag-offset',
      `${offset}px`
    );
  };

  const handleDragEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const dialog = dialogRef.current;
    const dragStartY = dragStartYRef.current;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!dialog || dragStartY === null) {
      resetDrag();
      return;
    }

    const offset = Math.max(0, event.clientY - dragStartY);
    const shouldClose = offset >= dialog.getBoundingClientRect().height / 4;
    resetDrag();
    if (shouldClose) {
      onOpenChange(false);
    }
  };

  return (
    <dialog
      id={id}
      ref={dialogRef}
      aria-describedby={description ? descriptionId : undefined}
      aria-labelledby={titleId}
      aria-modal="true"
      className={cn('cms-theme', `${drawerClassName}__content`)}
      data-cms-workspace-mobile-drawer="content"
      data-size={size}
      data-state={open ? 'open' : 'closed'}
      onAnimationEnd={finishCloseAnimation}
      onCancel={(event) => {
        event.preventDefault();
        onOpenChange(false);
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onOpenChange(false);
        }
      }}
    >
      <WorkspaceMobileDrawerOverlayHostContext.Provider value={overlayHost}>
        <NativeModalPortalHostProvider>
          <div
            aria-hidden="true"
            className={`${drawerClassName}__handle`}
            data-cms-workspace-mobile-drawer-handle=""
            onPointerCancel={handleDragEnd}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
          />
          <div
            className={cn(
              `${drawerClassName}__header`,
              header === 'sr-only' && 'sr-only'
            )}
          >
            <div className={`${drawerClassName}__title`} id={titleId}>
              {title}
            </div>
            {description ? (
              <p
                className={`${drawerClassName}__description`}
                id={descriptionId}
              >
                {description}
              </p>
            ) : null}
          </div>
          <div className={cn(`${drawerClassName}__body`, bodyClassName)}>
            {children}
          </div>
          <div
            ref={setOverlayHost}
            className={`${drawerClassName}__overlayHost`}
            data-cms-workspace-mobile-drawer-overlay-host=""
          />
        </NativeModalPortalHostProvider>
      </WorkspaceMobileDrawerOverlayHostContext.Provider>
    </dialog>
  );
}
